<script setup lang="ts">
import AttendanceParticipantPicker from "./AttendanceParticipantPicker.vue";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import {
  ATTENDANCE_MAX_HOURS,
  ATTENDANCE_STATUS_COLOR,
  ATTENDANCE_STATUS_ICON,
  ATTENDANCE_WARNED_STATUSES,
  signatureViewOf,
} from "~/utils/helpers/attendance/review-rows";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import type {
  IAttendanceParticipantOption,
  IAttendanceSignatureRow,
} from "~/utils/interfaces/attendance-review-interfaces";

const props = defineProps<{ row: IAttendanceSignatureRow }>();

/* The row is store state owned by the table: it hands the answers back rather than mutating here. */
const emit = defineEmits<{ pick: [IAttendanceParticipantOption | null]; updateHours: [number] }>();

const store = useAttendanceReviewStore();

const view = computed(() => signatureViewOf(props.row, store.enrolledUserIds));
</script>

<template>
  <td class="px-3 py-3 align-top">
    <!-- A minimum height lines the name up with the 40px picker opposite it, whatever the row's height. -->
    <div class="flex min-h-[40px] items-center gap-2">
      <SvgIcon
        :name="ATTENDANCE_STATUS_ICON[view.status]"
        class="h-[16px] w-[16px] shrink-0"
        :class="ATTENDANCE_STATUS_COLOR[view.status]"
      />
      <span class="body_regular text-primary-color">{{ row.nameAttendee }}</span>
    </div>
  </td>

  <td class="px-3 py-3 align-top">
    <div class="flex flex-col gap-1">
      <AttendanceParticipantPicker
        :row="row"
        :warning="ATTENDANCE_WARNED_STATUSES.includes(view.status)"
        @pick="emit('pick', $event)"
      />
      <span v-if="view.message" class="meta" :class="ATTENDANCE_STATUS_COLOR[view.status]">{{ view.message }}</span>
      <!-- An amber row spent its message on the identity; being unbooked still has to be said. -->
      <span v-if="view.enrolmentNote" class="meta text-primary-blue-color">{{ view.enrolmentNote }}</span>
    </div>
  </td>

  <td class="px-3 py-3 align-top">
    <div class="flex flex-col items-end gap-1">
      <div class="flex min-h-[40px] items-center justify-end gap-2">
        <div :class="{ hours__partial: view.missingHours > 0 }">
          <CommonInput
            :model-value="row.attendedHours"
            type="number"
            :min="0"
            :max="Math.min(row.totalHours, ATTENDANCE_MAX_HOURS)"
            placeholder="0"
            class="max-w-[72px]"
            @update:model-value="emit('updateHours', $event)"
          />
        </div>
        <span class="body_regular whitespace-nowrap text-tertiary-color">/ {{ row.totalHours }} h</span>
      </div>
      <!--
        The design also names the day the signature is missing ("absente le 13/03") — nothing in the
        proposal says which timeslot was missed, so only the deducted hours are stated.
      -->
      <span v-if="view.missingHours > 0" class="meta text-primary-orange-color">
        {{ view.missingHours }} h retirées
      </span>
    </div>
  </td>
</template>

<style lang="scss" scoped>
/* `CommonInput` only offers red through `error`; the design flags deducted hours in amber. */
.hours__partial :deep(.common_input__container) {
  border-color: var(--orange-main);
}
</style>
